import React, { useState, useEffect } from "react";
import Card from "components/card";
import { useNavigate } from "react-router-dom";
import {
  MdSearch,
  MdVisibility,
  MdFilterList,
  MdReceipt,
  MdLocalShipping,
  MdEdit,
  MdDelete,
  MdRefresh,
  MdClose,
  MdReportProblem,
  MdFileDownload,
} from "react-icons/md";
import toast from "react-hot-toast";
import { tokenManager } from "utils/tokenManager";
import { jwtDecode } from "jwt-decode";
import Loading from "components/loading";
import { useRcnDeliveries } from "hooks/useRcnDeliveries";
import { invalidateRcnDeliveriesCache } from "api/rcnDeliveries";

const API_BASE = "https://usibras-web-backend.onrender.com/api";

const RCN_TYPE_COLORS = {
  Organic: "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400",
  "Fair Trade": "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400",
  Conventional: "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400",
};

const STATUS_COLORS = {
  "In Transit": "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400",
  "Arrived at warehouse": "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400",
};

const formatDate = (dateStr) =>
  dateStr ? new Date(dateStr).toLocaleDateString("en-GB") : "—";

function ViewDetailsModal({ record, onClose }) {
  if (!record) return null;
  const r = record;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} aria-hidden />
      <div className="relative max-h-[90vh] w-full max-w-2xl overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-xl dark:border-navy-600 dark:bg-navy-800">
        <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4 dark:border-navy-600">
          <h3 className="text-lg font-bold text-navy-700 dark:text-white">
            Dispatch Details — {r.truck_number}
          </h3>
          <button
            onClick={onClose}
            className="rounded-lg p-2 text-gray-500 hover:bg-gray-100 dark:hover:bg-navy-700 dark:hover:text-white"
            aria-label="Close"
          >
            <MdClose className="h-6 w-6" />
          </button>
        </div>
        <div className="max-h-[70vh] overflow-y-auto p-6">
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
            <div>
              <p className="text-xs font-semibold uppercase text-gray-500 dark:text-gray-400">Date</p>
              <p className="font-medium text-navy-700 dark:text-white">{formatDate(r.dispatch_date)}</p>
            </div>
            <div>
              <p className="text-xs font-semibold uppercase text-gray-500 dark:text-gray-400">RCN Type</p>
              <span className={`inline-flex rounded-full px-2.5 py-0.5 text-sm font-medium ${RCN_TYPE_COLORS[r.rcn_type] || "bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-400"}`}>
                {r.rcn_type}
              </span>
            </div>
            <div>
              <p className="text-xs font-semibold uppercase text-gray-500 dark:text-gray-400">Truck Number</p>
              <p className="font-medium text-navy-700 dark:text-white">{r.truck_number}</p>
            </div>
            <div>
              <p className="text-xs font-semibold uppercase text-gray-500 dark:text-gray-400">Driver</p>
              <p className="font-medium text-navy-700 dark:text-white">{r.driver_name}</p>
            </div>
            <div>
              <p className="text-xs font-semibold uppercase text-gray-500 dark:text-gray-400">Driver Contact</p>
              <p className="font-medium text-navy-700 dark:text-white">{r.driver_contact || "—"}</p>
            </div>
            <div>
              <p className="text-xs font-semibold uppercase text-gray-500 dark:text-gray-400">Escort</p>
              <p className="font-medium text-navy-700 dark:text-white">{r.escort_name || "—"} {r.escort_contact ? `(${r.escort_contact})` : ""}</p>
            </div>
            <div>
              <p className="text-xs font-semibold uppercase text-gray-500 dark:text-gray-400">Warehouse</p>
              <p className="font-medium text-navy-700 dark:text-white">{r.warehouse_location || "—"}</p>
            </div>
            <div>
              <p className="text-xs font-semibold uppercase text-gray-500 dark:text-gray-400">Agent/Association</p>
              <p className="font-medium text-navy-700 dark:text-white">{r.agent || "—"}</p>
            </div>
            <div>
              <p className="text-xs font-semibold uppercase text-gray-500 dark:text-gray-400">Departure Weight (kg)</p>
              <p className="font-medium text-navy-700 dark:text-white">{r.weight != null ? Number(r.weight).toLocaleString() : "—"}</p>
            </div>
            <div>
              <p className="text-xs font-semibold uppercase text-gray-500 dark:text-gray-400"> Arrival Weight </p>
              <p className="font-medium text-navy-700 dark:text-white">{r.weight_of_arrival != null ? Number(r.weight_of_arrival).toLocaleString() : "—"}</p>
            </div>
            <div>
              <p className="text-xs font-semibold uppercase text-gray-500 dark:text-gray-400">Quantity</p>
              <p className="font-medium text-navy-700 dark:text-white">{r.quantity != null ? Number(r.quantity).toLocaleString() : "—"}</p>
            </div>
            <div>
              <p className="text-xs font-semibold uppercase text-gray-500 dark:text-gray-400">Moisture %</p>
              <p className="font-medium text-navy-700 dark:text-white">{r.moisture != null ? `${r.moisture}%` : "—"}</p>
            </div>
            <div>
              <p className="text-xs font-semibold uppercase text-gray-500 dark:text-gray-400">Analyst</p>
              <p className="font-medium text-navy-700 dark:text-white">{r.analyst_name || "—"}</p>
            </div>
            <div>
              <p className="text-xs font-semibold uppercase text-gray-500 dark:text-gray-400">Status</p>
              {r.status ? (
                <span className={`inline-flex rounded-full px-2.5 py-0.5 text-sm font-medium ${STATUS_COLORS[r.status] || "bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-400"}`}>
                  {r.status}
                </span>
              ) : (
                <p className="font-medium text-navy-700 dark:text-white">—</p>
              )}
            </div>
            {(r.picture_url || r.weighing_ticket_url || r.way_bill_url) && (
              <div className="sm:col-span-2 space-y-3">
                <p className="text-xs font-semibold uppercase text-gray-500 dark:text-gray-400">Attachments</p>
                <div className="flex flex-wrap gap-3">
                  {r.picture_url && (
                    <a href={r.picture_url} target="_blank" rel="noopener noreferrer" className="rounded-lg border border-gray-200 px-3 py-2 text-sm font-medium text-brand-500 hover:bg-brand-50 dark:border-navy-600 dark:hover:bg-navy-700">
                      Picture
                    </a>
                  )}
                  {r.weighing_ticket_url && (
                    <a href={r.weighing_ticket_url} target="_blank" rel="noopener noreferrer" className="rounded-lg border border-gray-200 px-3 py-2 text-sm font-medium text-brand-500 hover:bg-brand-50 dark:border-navy-600 dark:hover:bg-navy-700">
                      Weighing Ticket
                    </a>
                  )}
                  {r.way_bill_url && (
                    <a href={r.way_bill_url} target="_blank" rel="noopener noreferrer" className="rounded-lg border border-gray-200 px-3 py-2 text-sm font-medium text-brand-500 hover:bg-brand-50 dark:border-navy-600 dark:hover:bg-navy-700">
                      Way Bill
                    </a>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function DeleteConfirmModal({ record, onClose, onConfirm, isDeleting }) {
  if (!record) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} aria-hidden />
      <div className="relative w-full max-w-md rounded-2xl border border-gray-200 bg-white p-6 shadow-xl dark:border-navy-600 dark:bg-navy-800">
        <div className="mb-4 flex items-center gap-3">
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-red-100 dark:bg-red-900/30">
            <MdDelete className="h-6 w-6 text-red-600 dark:text-red-400" />
          </div>
          <div>
            <h3 className="text-lg font-bold text-navy-700 dark:text-white">Delete dispatch record?</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">This action cannot be undone.</p>
          </div>
        </div>
        <div className="mb-6 rounded-lg bg-gray-50 p-4 dark:bg-navy-700/50">
          <p className="text-sm font-medium text-navy-700 dark:text-white">{record.truck_number} — {record.agent || record.driver_name}</p>
          <p className="text-xs text-gray-500 dark:text-gray-400">{formatDate(record.dispatch_date)} • {record.rcn_type}</p>
        </div>
        <div className="flex justify-end gap-3">
          <button
            onClick={onClose}
            disabled={isDeleting}
            className="rounded-lg border border-gray-200 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-navy-600 dark:text-gray-300 dark:hover:bg-navy-700 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={() => onConfirm(record.id)}
            disabled={isDeleting}
            className="rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50"
          >
            {isDeleting ? "Deleting…" : "Delete"}
          </button>
        </div>
      </div>
    </div>
  );
}

const DispatchRecords = () => {
  const navigate = useNavigate();
  const { dispatches: records, loading, error, refetch: fetchRecords } = useRcnDeliveries();
  const [refreshToastId, setRefreshToastId] = useState(null);
  const [userRole, setUserRole] = useState("");
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    const loginData = localStorage.getItem("usibras_login");
    const userData = localStorage.getItem("usibras_user_data");
    if (loginData || userData) {
      try {
        let decoded;
        if (loginData) {
          try {
            const parsed = JSON.parse(loginData);
            decoded = parsed.userData ? jwtDecode(parsed.userData) : jwtDecode(loginData);
          } catch {
            decoded = jwtDecode(loginData);
          }
        } else {
          decoded = jwtDecode(userData);
        }
        if (decoded?.role) setUserRole(decoded.role);
      } catch (err) {
        console.error("Error decoding role:", err);
      }
    }
  }, []);
  const [filters, setFilters] = useState({
    startDate: "",
    endDate: "",
    rcnType: "",
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [showFilters, setShowFilters] = useState(false);
  const [viewRecord, setViewRecord] = useState(null);
  const [deleteRecord, setDeleteRecord] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const itemsPerPage = 10;

  const filteredRecords = records.filter((r) => {
    const matchesSearch =
      !searchTerm ||
      (r.truck_number?.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (r.driver_name?.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (r.warehouse_location?.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (r.analyst_name?.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (r.agent?.toLowerCase().includes(searchTerm.toLowerCase()));

    const matchesDate =
      (!filters.startDate || new Date(r.dispatch_date) >= new Date(filters.startDate)) &&
      (!filters.endDate || new Date(r.dispatch_date) <= new Date(filters.endDate));

    const matchesType = !filters.rcnType || r.rcn_type === filters.rcnType;

    return matchesSearch && matchesDate && matchesType;
  });

  const totalPages = Math.ceil(filteredRecords.length / itemsPerPage) || 1;
  const paginatedRecords = filteredRecords.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const handleEdit = (record) => {
    navigate("/admin/rcn-delivery/form", { state: { editRecord: record } });
  };

  const handleDeleteConfirm = async (id) => {
    setIsDeleting(true);
    try {
      const authHeader = tokenManager.getAuthHeader();
      if (!authHeader) {
        toast.error("Authentication required");
        setIsDeleting(false);
        return;
      }
      const delToastId = toast.loading("Deleting...");
      const resp = await fetch(`${API_BASE}/rcn-deliveries/${id}`, {
        method: "DELETE",
        headers: { Authorization: authHeader },
      });
      const result = await resp.json().catch(() => ({}));
      if (resp.ok) {
        toast.success(result.message || "Record deleted", { id: delToastId });
        setDeleteRecord(null);
        invalidateRcnDeliveriesCache();
        fetchRecords();
      } else {
        toast.error(result.message || result.error || "Failed to delete", { id: delToastId });
      }
    } catch (err) {
      toast.error(err.message || "Failed to delete");
    } finally {
      setIsDeleting(false);
    }
  };

  const exportToCSV = () => {
    if (filteredRecords.length === 0) {
      toast.error("No records to export");
      return;
    }

    const headers = [
      "Date",
      "RCN Type",
      "Truck Number",
      "Agent/Association",
      "Driver",
      "Driver Contact",
      "Escort",
      "Escort Contact",
      "Warehouse",
      "Departure Weight (kg)",
      "Arrival Weight (kg)",
      "Quantity",
      "Moisture (%)",
      "Analyst",
      "Status",
    ];

    const escapeCSV = (value) => {
      if (value == null) return "";
      const str = String(value);
      if (str.includes(",") || str.includes('"') || str.includes("\n")) {
        return `"${str.replace(/"/g, '""')}"`;
      }
      return str;
    };

    const rows = filteredRecords.map((r) => [
      formatDate(r.dispatch_date),
      r.rcn_type || "",
      r.truck_number || "",
      r.agent || "",
      r.driver_name || "",
      r.driver_contact || "",
      r.escort_name || "",
      r.escort_contact || "",
      r.warehouse_location || "",
      r.weight != null ? r.weight : "",
      r.weight_of_arrival != null ? r.weight_of_arrival : "",
      r.quantity != null ? r.quantity : "",
      r.moisture != null ? r.moisture : "",
      r.analyst_name || "",
      r.status || "",
    ]);

    const csvContent = [
      headers.map(escapeCSV).join(","),
      ...rows.map((row) => row.map(escapeCSV).join(",")),
    ].join("\n");

    const blob = new Blob(["\uFEFF" + csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `dispatch_records_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    toast.success(`Exported ${filteredRecords.length} record(s)`);
  };

  const handleRefresh = () => {
    if (loading) return;
    const id = toast.loading("Refreshing...");
    setRefreshToastId(id);
    fetchRecords();
  };

  useEffect(() => {
    if (refreshToastId == null) return;
    if (!loading) {
      if (error) {
        toast.error(error || "Failed to refresh", { id: refreshToastId });
      } else {
        toast.success("Refreshed", { id: refreshToastId });
      }
      setRefreshToastId(null);
    }
  }, [loading, error, refreshToastId]);

  return (
    <div className="mt-3">
      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <h2 className="text-2xl font-bold text-navy-700 dark:text-white">
          Delivery Dispatch Records
        </h2>
        <div className="flex gap-2">
          <button
            onClick={exportToCSV}
            disabled={loading || filteredRecords.length === 0}
            className="flex items-center gap-2 rounded-lg border border-gray-200 px-4 py-2.5 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 dark:border-navy-600 dark:text-gray-300 dark:hover:bg-navy-700 disabled:opacity-50"
          >
            <MdFileDownload className="h-5 w-5" />
            Export CSV
          </button>
          <button
            onClick={fetchRecords}
            disabled={loading}
            className="flex items-center gap-2 rounded-lg border border-gray-200 px-4 py-2.5 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 dark:border-navy-600 dark:text-gray-300 dark:hover:bg-navy-700 disabled:opacity-50"
          >
            <MdRefresh className={`h-5 w-5 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </button>
          <button
            onClick={() => navigate("/admin/rcn-delivery/form")}
            className="flex items-center gap-2 rounded-lg bg-brand-500 px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-brand-600"
          >
            <MdLocalShipping className="h-5 w-5" />
            New Dispatch
          </button>
        </div>
      </div>

      {/* Status Summary Cards */}
      <div className="mb-6 grid grid-cols-2 gap-4 sm:grid-cols-3">
        <div className="rounded-xl border border-gray-200 bg-white p-4 dark:border-navy-600 dark:bg-navy-800">
          <p className="text-xs font-semibold uppercase text-gray-500 dark:text-gray-400">Total</p>
          <p className="mt-1 text-2xl font-bold text-navy-700 dark:text-white">{records.length}</p>
        </div>
        <div className="rounded-xl border border-blue-200 bg-blue-50 p-4 dark:border-blue-800 dark:bg-blue-900/20">
          <p className="text-xs font-semibold uppercase text-blue-600 dark:text-blue-400">In Transit</p>
          <p className="mt-1 text-2xl font-bold text-blue-700 dark:text-blue-300">
            {records.filter((r) => r.status === "In Transit").length}
          </p>
        </div>
        <div className="rounded-xl border border-green-200 bg-green-50 p-4 dark:border-green-800 dark:bg-green-900/20">
          <p className="text-xs font-semibold uppercase text-green-600 dark:text-green-400">Arrived</p>
          <p className="mt-1 text-2xl font-bold text-green-700 dark:text-green-300">
            {records.filter((r) => r.status === "Arrived at warehouse").length}
          </p>
        </div>
      </div>

      <Card extra="overflow-hidden p-6">
        <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center">
          <div className="relative flex-1">
            <MdSearch className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search by truck, driver, warehouse, analyst, agent..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
              className="w-full rounded-lg border border-gray-200 py-2.5 pl-10 pr-4 text-sm outline-none focus:border-brand-500 dark:border-navy-600 dark:bg-navy-800 dark:text-white dark:placeholder:text-gray-400"
            />
          </div>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`flex items-center gap-2 rounded-lg border px-4 py-2.5 text-sm font-medium transition-colors ${
              showFilters
                ? "border-brand-500 bg-brand-50 text-brand-600 dark:bg-brand-500/20 dark:text-brand-400"
                : "border-gray-200 text-gray-700 hover:bg-gray-50 dark:border-navy-600 dark:text-gray-300 dark:hover:bg-navy-700"
            }`}
          >
            <MdFilterList className="h-5 w-5" />
            Filters
          </button>
        </div>

        {showFilters && (
          <div className="mb-6 flex flex-wrap gap-4 rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-navy-600 dark:bg-navy-800/50">
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400">From Date</label>
              <input
                type="date"
                value={filters.startDate}
                onChange={(e) => {
                  setFilters((f) => ({ ...f, startDate: e.target.value }));
                  setCurrentPage(1);
                }}
                className="rounded-lg border border-gray-200 px-3 py-2 text-sm dark:border-navy-600 dark:bg-navy-800 dark:text-white"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400">To Date</label>
              <input
                type="date"
                value={filters.endDate}
                onChange={(e) => {
                  setFilters((f) => ({ ...f, endDate: e.target.value }));
                  setCurrentPage(1);
                }}
                className="rounded-lg border border-gray-200 px-3 py-2 text-sm dark:border-navy-600 dark:bg-navy-800 dark:text-white"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400">RCN Type</label>
              <select
                value={filters.rcnType}
                onChange={(e) => {
                  setFilters((f) => ({ ...f, rcnType: e.target.value }));
                  setCurrentPage(1);
                }}
                className="rounded-lg border border-gray-200 px-3 py-2 text-sm dark:border-navy-600 dark:bg-navy-800 dark:text-white"
              >
                <option value="">All types</option>
                <option value="Organic">Organic</option>
                <option value="Fair Trade">Fair Trade</option>
                <option value="Conventional">Conventional</option>
              </select>
            </div>
          </div>
        )}

        {loading ? (
          <Loading text="Loading records…" />
        ) : error ? (
          <div className="flex flex-col items-center justify-center py-16">
            <p className="text-sm text-red-500">{error}</p>
            <button
              onClick={fetchRecords}
              className="mt-4 flex items-center gap-2 text-sm font-medium text-brand-500 hover:underline"
            >
              <MdRefresh className="h-5 w-5" />
              Retry
            </button>
          </div>
        ) : (
          <>
            <p className="mb-4 text-sm text-gray-600 dark:text-gray-400">
              Showing {filteredRecords.length} record(s)
            </p>

            <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-navy-600">
              <table className="w-full min-w-[1200px]">
                <thead>
                  <tr className="border-b border-gray-200 bg-gray-50 dark:border-navy-600 dark:bg-navy-800/50">
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">Date</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">RCN Type</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">Truck</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">Agent/Association</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">Warehouse</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">Departure Weight</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">Qty</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">Moisture</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">Arrival Weight</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">Analyst</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">Status</th>
                    <th className="px-4 py-3 text-center text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedRecords.length === 0 ? (
                    <tr>
                      <td colSpan={12} className="py-16 text-center">
                        <MdReceipt className="mx-auto mb-2 h-14 w-14 text-gray-300 dark:text-gray-600" />
                        <p className="text-gray-500 dark:text-gray-400">No dispatch records found</p>
                      </td>
                    </tr>
                  ) : (
                    paginatedRecords.map((r) => (
                      <tr
                        key={r.id}
                        className="border-b border-gray-100 transition-colors hover:bg-gray-50 dark:border-navy-700 dark:hover:bg-navy-800/30"
                      >
                        <td className="whitespace-nowrap px-4 py-4 text-sm font-medium text-navy-700 dark:text-white">{formatDate(r.dispatch_date)}</td>
                        <td className="whitespace-nowrap px-4 py-4">
                          <span className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium ${RCN_TYPE_COLORS[r.rcn_type] || "bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-400"}`}>
                            {r.rcn_type}
                          </span>
                        </td>
                        <td className="whitespace-nowrap px-4 py-4 text-sm text-navy-700 dark:text-white">{r.truck_number}</td>
                        <td className="whitespace-nowrap px-4 py-4 text-sm text-navy-700 dark:text-white">{r.agent || "—"}</td>
                        <td className="whitespace-nowrap px-4 py-4 text-sm text-navy-700 dark:text-white">{r.warehouse_location || "—"}</td>
                        <td className="whitespace-nowrap px-4 py-4 text-right text-sm text-navy-700 dark:text-white">{r.weight != null ? Number(r.weight).toLocaleString() : "—"}</td>
                        <td className="whitespace-nowrap px-4 py-4 text-right text-sm text-navy-700 dark:text-white">{r.quantity != null ? Number(r.quantity).toLocaleString() : "—"}</td>
                        <td className="whitespace-nowrap px-4 py-4 text-right text-sm text-navy-700 dark:text-white">{r.moisture != null ? `${r.moisture}%` : "—"}</td>
                        <td className="whitespace-nowrap px-4 py-4 text-right text-sm text-navy-700 dark:text-white">{r.weight_of_arrival != null ? Number(r.weight_of_arrival).toLocaleString() : "—"}</td>
                        <td className="whitespace-nowrap px-4 py-4 text-sm text-navy-700 dark:text-white">{r.analyst_name || "—"}</td>
                        <td className="whitespace-nowrap px-4 py-4">
                          {r.status ? (
                            <span className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium ${STATUS_COLORS[r.status] || "bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-400"}`}>
                              {r.status}
                            </span>
                          ) : (
                            <span className="text-sm text-navy-700 dark:text-white">—</span>
                          )}
                        </td>
                        <td className="whitespace-nowrap px-4 py-4">
                          <div className="flex items-center justify-center gap-1">
                            <button
                              onClick={() => setViewRecord(r)}
                              className="rounded-lg p-2 text-gray-600 transition-colors hover:bg-brand-50 hover:text-brand-600 dark:text-gray-400 dark:hover:bg-brand-500/20 dark:hover:text-brand-400"
                              title="View details"
                            >
                              <MdVisibility className="h-5 w-5" />
                            </button>
                            <button
                              onClick={() => handleEdit(r)}
                              className="rounded-lg p-2 text-gray-600 transition-colors hover:bg-amber-50 hover:text-amber-600 dark:text-gray-400 dark:hover:bg-amber-500/20 dark:hover:text-amber-400"
                              title="Edit"
                            >
                              <MdEdit className="h-5 w-5" />
                            </button>
                            {userRole === "admin" && (
                              <button
                                onClick={() => setDeleteRecord(r)}
                                className="rounded-lg p-2 text-gray-600 transition-colors hover:bg-red-50 hover:text-red-600 dark:text-gray-400 dark:hover:bg-red-500/20 dark:hover:text-red-400"
                                title="Delete"
                              >
                                <MdDelete className="h-5 w-5" />
                              </button>
                            )}
                            <button
                              onClick={() => navigate("/admin/rcn-delivery/incidence-report", { state: { record: r } })}
                              className="rounded-lg p-2 text-gray-600 transition-colors hover:bg-orange-50 hover:text-orange-600 dark:text-gray-400 dark:hover:bg-orange-500/20 dark:hover:text-orange-400"
                              title="Incidence Report"
                            >
                              <MdReportProblem className="h-5 w-5" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {totalPages > 1 && (
              <div className="mt-6 flex items-center justify-between border-t border-gray-200 pt-4 dark:border-navy-600">
                <p className="text-sm text-gray-600 dark:text-gray-400">Page {currentPage} of {totalPages}</p>
                <div className="flex gap-2">
                  <button
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    className="rounded-lg border border-gray-200 px-3 py-1.5 text-sm font-medium text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:border-navy-600 dark:text-gray-300 dark:hover:bg-navy-700"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                    className="rounded-lg border border-gray-200 px-3 py-1.5 text-sm font-medium text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:border-navy-600 dark:text-gray-300 dark:hover:bg-navy-700"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </Card>

      {viewRecord && <ViewDetailsModal record={viewRecord} onClose={() => setViewRecord(null)} />}
      {deleteRecord && (
        <DeleteConfirmModal
          record={deleteRecord}
          onClose={() => setDeleteRecord(null)}
          onConfirm={handleDeleteConfirm}
          isDeleting={isDeleting}
        />
      )}
    </div>
  );
};

export default DispatchRecords;
